import { makeVar, useQuery, useReactiveVar } from "@apollo/client";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { graphql, type ResultOf } from "@/graphql";
import { removeNothings } from "@/lib/utils/arrays";

type BusinessUnitId = string;

const ACTIVE_BUSINESS_UNIT_KEY = "businessUnitId" as const;

export const activeBusinessUnitIdVar = makeVar<BusinessUnitId | undefined>(
  localStorage.getItem(ACTIVE_BUSINESS_UNIT_KEY) || undefined,
);

export const useActiveBusinessUnit = () => {
  const activeBusinessUnitId = useReactiveVar(activeBusinessUnitIdVar);
  const router = useRouter();

  const setActiveBusinessUnitId = useCallback(
    (id: BusinessUnitId | null) => {
      if (!id) {
        return;
      }

      localStorage.setItem(ACTIVE_BUSINESS_UNIT_KEY, id);
      activeBusinessUnitIdVar(id);
      // Navigate to root when changing business unit
      void router.navigate({ to: "/", replace: true });
    },
    [router],
  );

  const { data, loading } = useQuery(businessUnitsQuery);

  const businessUnits = removeNothings(data?.businessUnits || []);

  // If we don't have an active business unit and we got data, set the first one
  useEffect(() => {
    const defaultBu = businessUnits[0];

    if (!activeBusinessUnitId && defaultBu) {
      setActiveBusinessUnitId(defaultBu.id);
    }
  }, [activeBusinessUnitId, businessUnits, setActiveBusinessUnitId]);

  const activeBusinessUnit = businessUnits.find((bu) => bu.id === activeBusinessUnitId);

  return {
    activeBusinessUnitId,
    activeBusinessUnitIdInt: activeBusinessUnitId ? parseInt(activeBusinessUnitId) : undefined,
    activeBusinessUnit,
    /**
     * ISO alpha-2 code of the Business Unit's own country. Nullable on the API,
     * so it is never guessed — an absent country leaves the picker empty.
     */
    activeBusinessUnitCountryCode: activeBusinessUnit?.country?.isoAlpha2,
    setActiveBusinessUnitId,
    businessUnits,
    loading,
  };
};

export type BusinessUnits = ResultOf<typeof businessUnitsQuery>["businessUnits"];

export const businessUnitsQuery = graphql(`
  query BusinessUnitsQuery {
    businessUnits {
      id
      name
      country {
        id
        code
        isoAlpha2
      }
      region {
        code
        name
      }
    }
  }
`);
